import IconButton from "@able/react/dist/IconButton";
import "./style.scss";
import { useEffect, useState } from "react";
import { AbleCommonProps } from "../Types";
import SearchIcon from "../assets/images/iconSearch24.svg";
import Spacing from "@able/react/dist/Spacing";
import Icon from "@able/react/dist/Icon";
import TextStyle from "@able/react/dist/TextStyle";
import { ableTextAlias } from "../util/able-migration";
import { isEvent } from "../util/helpers";
import { KEY_MAPS } from "../util/_event.map";

export type FilterByStringProps = {
    id: string;
    invalid?: boolean;
    errorMsg?: string;
    label?: string;
    helpText?: string;
    getQueryText: (value: string) => void;
    variant: 'default' | 'compact'
    searchBtnVariant?: 'highEmphasis' | 'mediumEmphasis'
    hasSearchButton: boolean;
    customSearchLength?: number;
    placeholderText?: string;
    ariaDescribedBy?: string;
    value: string;
    elementRef?: React.RefObject<HTMLInputElement>;
    onEnter?: (value: string) => void;
} & AbleCommonProps;

export const FilterByString = (props: FilterByStringProps) => {
    const { developmentUrl, getQueryText, hasSearchButton, customSearchLength = 1, placeholderText, variant, searchBtnVariant, ariaDescribedBy, id, invalid = false, label, errorMsg, helpText, value = "", elementRef, onEnter } = props;

    const [input, setInput] = useState<string>("");
    const isHighEmphasis = searchBtnVariant === 'highEmphasis'
    const isDefault = variant === 'default';
    const getVariantClass = isDefault ? 'input_default' : 'input_compact';

    const getVariantClassHighEmph = isDefault ? 'able-ActionButton--high-emphasis_default' : 'able-ActionButton--high-emphasis_compact';
    const getVariantClassMediumEmph = isDefault ? 'able-ActionButton--medium-emphasis_default' : 'able-ActionButton--medium-emphasis_compact';

    const showErrorMsg = invalid && errorMsg;
    const inputStateClass = showErrorMsg ? "input-error" : "input-active";

    useEffect(() => {
        setInput(value);
    }, [value]);

    const handleInputChange = (input: string) => {
        setInput(input);
        if (!hasSearchButton) {
            if (customSearchLength) {
                if (customSearchLength <= input.length) {
                    return getQueryText(input);
                } else {
                    return getQueryText("");
                }
            } else {
                getQueryText(input);
            }
        } else {
            return null
        }
    };

    const handleClear = () => {
        getQueryText("");
        setInput("");
        (document?.getElementById(`${id}-filterByString-input`) as HTMLInputElement).focus();
    };

    const onClickSearchButton = () => {
        setInput(input)
        if (customSearchLength) {
            if (customSearchLength <= input.length) {
                return getQueryText(input);
            } else {
                return null
            }
        } else {
            getQueryText(input);
        }
    }

    const dispalyCrossIcon = () => {
        return (
            <IconButton
                icon="Close"
                size="Default"
                developmentUrl={developmentUrl}
                events={{
                    onClick: () => handleClear(),
                }}
                className="cross-icon-button"
                screenReaderContent="Clear"
                data-testid="clearIcon"
            />
        )
    }

    const HeaderComponent = () => {
        return (
            <>
                {label && <label htmlFor={`${id}-filterByString-input`} className="fbs-label">
                    {label}
                </label>}
                <Spacing bottom="spacingHalf" />
                {helpText && (
                    <div className="help-text" id={`${id}-help-text`}>
                        {helpText}
                    </div>
                )}
            </>
        )
    }

    const ErrorComponent = () => {
        return (
            <>
                <Spacing bottom="spacingHalf" />
                <div
                    role="alert"
                    id={`${id}-error-text`}
                    className="error-container"
                >
                    <Icon
                        icon="Error"
                        size="24"
                        developmentUrl={developmentUrl}
                        className="error-icon"
                        screenReaderContent="Error!"
                    />
                    {errorMsg && <TextStyle
                        alias={ableTextAlias("FinePrint")}
                        colour="Subtle"
                        className="error-msg"
                    >
                        {errorMsg}
                    </TextStyle>}
                </div>
            </>
        )
    }
    const ariaDescribedByVal = `${ariaDescribedBy ? ariaDescribedBy : ''} ${helpText ? `${id}-help-text` : ''} ${showErrorMsg ? `${id}-error-text` : ''}`.trim();
    return (
        <div role="search" className="ted-ui-filterByString">
            <HeaderComponent />
            <Spacing bottomVSM="spacing1x" bottomVXS="spacing1x" bottomVMD="spacing1x" bottomVLG="spacing1nHalf" />
            <div className="ted-ui-fbs-input-container">
                <div className="input-wrapper">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={(e)=> {
                            if(isEvent(e,KEY_MAPS.Enter)) {
                                onEnter && onEnter(input);
                            }
                        }}
                        placeholder={!label ? placeholderText : ''}
                        className={`${hasSearchButton ? `input input_withSearchBtn ${getVariantClass} ${inputStateClass}` : `input input_withoutSearchBtn input_indent ${getVariantClass} ${inputStateClass}`}`}
                        aria-label={!label ? placeholderText : ''}
                        data-testid="input"
                        aria-describedby={ariaDescribedByVal}
                        id={`${id}-filterByString-input`}
                        aria-invalid={showErrorMsg ? true : false}
                        ref={elementRef}
                    />
                    {!hasSearchButton && <span className="search-icon">
                        <SearchIcon role="img" aria-hidden="true" />
                    </span>}
                    {input.length > 0 &&
                        <span className='icon-container'>
                            {dispalyCrossIcon()}
                        </span>
                    }
                </div>
                {hasSearchButton &&
                    <button type="button" onClick={onClickSearchButton} className={`${isHighEmphasis ? `able-ActionButton able-ActionButton--high-emphasis ${getVariantClassHighEmph}`
                        : `able-ActionButton able-ActionButton--medium-emphasis ${getVariantClassMediumEmph}`}`} data-testid="searchBtn">
                        <svg className="able-icon able-icon--24" role="img" aria-hidden="false" aria-label="Search" focusable="false">
                            <use href={`${developmentUrl}#Search`}>
                            </use>
                        </svg>
                    </button>
                }
            </div>
            {showErrorMsg && <ErrorComponent />}
        </div>
    );
};

export default FilterByString;
